import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { FaCheckCircle, FaIdCard, FaDownload, FaEnvelope, FaPhone, FaUserTie } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Filières LICENCE
const filieresLicence = [
  "Droit",
  "Communication",
  "Sciences et Techniques Informatiques",
  "Histoire",
  "Lettres Modernes",
  "Mathématiques option Actuariat",
  "Logistique",
  "Management Marketing",
  "Science Politique",
  "Sciences Comptables et Financières",
  "Sciences Économiques",
  "Géographie et Environnement",
  "Sciences Physiques",
  "Gestion des Ressources Humaines"
];

// Filières MASTER
const filieresMaster = [
  "Droit des Affaires et Fiscalité des Entreprises",
  "Droit International et Humanitaire",
  "Droit Maritime et Activités Portuaires",
  "Finance",
  "Logistique",
  "Sciences Économiques option Monnaie, Banque et Finance",
  "Sciences Comptables",
  "Science Politique",
  "Gestion des Risques en Assurance et Finance",
  "Marketing",
  "Gestion des Ressources Humaines",
  "Économie du Développement",
  "Géomatique"
];

const campus = [
  "Abidjan Cocody-Angré",
  "Abengourou",
  "Bondoukou",
  "Bonoua",
  "Accra (Ghana)"
];

const derniersDiplomes = [
  "BAC",
  "DUT",
  "BTS",
  "LICENCE",
  "BACHELOR",
  "MAITRISE",
  "MASTER",
  "DOCTORAT / PhD"
];

// Diplômes sollicités Licence
const diplomesSollicitesLicence = ["L1", "L2", "L3"];

// Diplômes sollicités Master
const diplomesSollicitesMaster = ["M1", "M2"];

export default function PreInscription() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [matricule, setMatricule] = useState('');
  const [formationType, setFormationType] = useState(''); // 'licence' ou 'master'
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    whatsapp: '',
    filiere: '',
    campus: '',
    dernier_diplome: '',
    diplome_sollicite: '',
    type_formation: ''
  });

  // Réinitialiser la filière quand le type de formation change
  useEffect(() => {
    if (formationType) {
      setFormData(prev => ({
        ...prev,
        filiere: '',
        diplome_sollicite: '',
        type_formation: formationType
      }));
    }
  }, [formationType]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const getCurrentFilieres = () => {
    return formationType === 'licence' ? filieresLicence : formationType === 'master' ? filieresMaster : [];
  };

  const getCurrentDiplomesSollicites = () => {
    return formationType === 'licence' ? diplomesSollicitesLicence : formationType === 'master' ? diplomesSollicitesMaster : [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nom || !formData.prenom || !formData.email || !formData.whatsapp || 
        !formData.filiere || !formData.campus || !formData.dernier_diplome || !formData.diplome_sollicite || !formationType) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/pre-registrations`, {
        ...formData,
        type_formation: formationType
      });
      
      if (response.status === 200) {
        setMatricule(response.data.matricule);
        setSuccess(true);
        toast.success('Préinscription envoyée avec succès!');
      }
    } catch (error) {
      console.error('Erreur lors de la préinscription:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Page de confirmation après préinscription
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 sm:py-16 print:bg-white print:py-4">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-6 sm:p-10 shadow-xl bg-white print:shadow-none print:border print:border-gray-300">
            {/* En-tête avec logo UCI - visible à l'impression */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-brand-primary">
              <div className="flex items-center gap-4">
                <img 
                  src="/logo-uci.png" 
                  alt="Logo UCI" 
                  className="h-16 sm:h-20 w-auto"
                />
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-brand-secondary">Université CEFAT International</h2>
                  <p className="text-sm text-brand-primary font-semibold">« L'excellence à proximité »</p>
                </div>
              </div>
              <div className="hidden print:block text-right text-sm text-gray-600">
                <p>Tél: 07 12 65 17 19</p>
                <p>contact@universitecefat.ci</p>
              </div>
            </div>

            {/* Succès Header - masqué à l'impression */}
            <div className="text-center mb-8 print:hidden">
              <FaCheckCircle className="text-6xl sm:text-7xl text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl sm:text-3xl font-bold text-brand-secondary mb-2">
                Préinscription Réussie !
              </h1>
              <p className="text-gray-600">
                Bienvenue à l'Université CEFAT International
              </p>
            </div>

            {/* Titre pour l'impression */}
            <div className="hidden print:block text-center mb-6">
              <h1 className="text-2xl font-bold text-brand-secondary">FICHE DE PRÉINSCRIPTION</h1>
              <p className="text-gray-600">Année académique 2025-2026</p>
            </div>
            
            {/* Matricule */}
            <div className="bg-gradient-to-r from-brand-primary to-brand-primary-dark rounded-xl p-6 mb-8 text-white text-center print:bg-white print:text-brand-primary print:border-2 print:border-brand-primary">
              <div className="flex items-center justify-center gap-3 mb-2">
                <FaIdCard className="text-2xl" />
                <span className="text-lg font-semibold">Votre Matricule</span>
              </div>
              <p className="text-3xl sm:text-4xl font-bold tracking-wider">
                {matricule}
              </p>
              <p className="text-sm text-white/80 mt-2 print:text-gray-600">
                Conservez ce numéro précieusement pour votre inscription définitive
              </p>
            </div>

            {/* Récapitulatif */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8 print:bg-white print:border print:border-gray-300">
              <h3 className="font-bold text-lg text-brand-secondary mb-4">Récapitulatif de votre préinscription</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Nom complet</p>
                  <p className="font-semibold text-gray-800">{formData.nom} {formData.prenom}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-semibold text-gray-800">{formData.email}</p>
                </div>
                <div>
                  <p className="text-gray-500">WhatsApp</p>
                  <p className="font-semibold text-gray-800">{formData.whatsapp}</p>
                </div>
                <div>
                  <p className="text-gray-500">Campus</p>
                  <p className="font-semibold text-gray-800">{formData.campus}</p>
                </div>
                <div>
                  <p className="text-gray-500">Type de Formation</p>
                  <p className="font-semibold text-gray-800 uppercase">{formationType}</p>
                </div>
                <div>
                  <p className="text-gray-500">Filière</p>
                  <p className="font-semibold text-gray-800">{formData.filiere}</p>
                </div>
                <div>
                  <p className="text-gray-500">Dernier Diplôme</p>
                  <p className="font-semibold text-gray-800">{formData.dernier_diplome}</p>
                </div>
                <div>
                  <p className="text-gray-500">Diplôme Sollicité</p>
                  <p className="font-semibold text-gray-800">{formData.diplome_sollicite}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-gray-500">Date de préinscription</p>
                  <p className="font-semibold text-gray-800">{new Date().toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
            </div>

            {/* Documents à télécharger - masqué à l'impression */}
            <div className="mb-8 print:hidden">
              <h3 className="font-bold text-lg text-brand-secondary mb-4">Documents à télécharger</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a 
                  href="https://customer-assets.emergentagent.com/job_cefat-university/artifacts/lxh92yrb_fiche%20de%20renseignements%20UCI.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  <FaDownload className="text-2xl text-blue-600" />
                  <div>
                    <p className="font-semibold text-blue-800">Fiche de Renseignements</p>
                    <p className="text-xs text-blue-600">À remplir et retourner</p>
                  </div>
                </a>
                <button 
                  onClick={() => window.print()}
                  className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors text-left"
                >
                  <FaDownload className="text-2xl text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">Fiche de Préinscription</p>
                    <p className="text-xs text-green-600">Imprimer cette page</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Contact Conseiller - version impression */}
            <div className="bg-brand-secondary/5 border border-brand-secondary/20 rounded-xl p-6 mb-8 print:bg-white print:border-gray-300">
              <div className="flex items-center gap-3 mb-4">
                <FaUserTie className="text-2xl text-brand-secondary print:hidden" />
                <h3 className="font-bold text-lg text-brand-secondary">Votre Conseiller d'Orientation</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Pour toute question concernant votre inscription, contactez votre conseiller :
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-brand-primary">
                  <FaEnvelope className="text-lg print:hidden" />
                  <span className="font-medium">conseiller@universitecefat.ci</span>
                </div>
                <div className="flex items-center gap-3 text-brand-primary">
                  <FaPhone className="text-lg print:hidden" />
                  <span className="font-medium">07 58 14 07 95</span>
                </div>
              </div>
            </div>

            {/* Prochaines étapes - masqué à l'impression */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8 print:hidden">
              <h4 className="font-bold text-amber-800 mb-3">Prochaines étapes</h4>
              <ol className="list-decimal list-inside text-sm text-amber-700 space-y-2">
                <li>Téléchargez et remplissez la <strong>Fiche de Renseignements</strong></li>
                <li>Préparez vos documents (copie du diplôme, pièce d'identité, photos)</li>
                <li>Contactez votre <strong>conseiller d'orientation</strong> pour finaliser votre dossier</li>
                <li>Effectuez le paiement des frais d'inscription</li>
                <li>Recevez votre confirmation d'admission</li>
              </ol>
            </div>

            {/* Signature pour impression */}
            <div className="hidden print:block mb-8 mt-8">
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-12">Signature de l'étudiant</p>
                  <div className="border-t border-gray-400 pt-2">
                    <p className="text-xs text-gray-500">Date: ___ / ___ / ______</p>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-12">Cachet et signature de l'université</p>
                  <div className="border-t border-gray-400 pt-2">
                    <p className="text-xs text-gray-500">Date: ___ / ___ / ______</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact général - masqué à l'impression */}
            <div className="text-center bg-gray-100 rounded-lg p-4 mb-6 print:hidden">
              <p className="text-gray-700 text-sm mb-2">Contact général de l'université :</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 text-brand-primary font-semibold">
                <a href="tel:0712651719" className="hover:underline">07 12 65 17 19</a>
                <a href="tel:0100959518" className="hover:underline">01 00 95 95 18</a>
              </div>
              <p className="text-xs text-gray-500 mt-2">contact@universitecefat.ci</p>
            </div>

            <div className="text-center print:hidden">
              <Button
                onClick={() => navigate('/')}
                className="bg-brand-primary hover:bg-brand-primary-dark text-white px-8 py-3"
              >
                Retour à l'accueil
              </Button>
            </div>

            {/* Pied de page pour impression */}
            <div className="hidden print:block text-center mt-8 pt-4 border-t border-gray-300 text-xs text-gray-500">
              <p>Université CEFAT International - « L'excellence à proximité »</p>
              <p>Abidjan Cocody-Angré | Abengourou | Bondoukou | Bonoua | Accra (Ghana)</p>
              <p>www.universitecefat.ci | contact@universitecefat.ci</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-brand-secondary">Préinscription</h1>
          <p className="text-base sm:text-lg text-gray-600">Rejoignez l'Université CEFAT International</p>
          <p className="text-brand-primary font-semibold mt-2">« L'excellence à proximité »</p>
        </div>

        <Card className="p-6 sm:p-8 md:p-12 shadow-xl bg-white">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Choix du type de formation */}
            <div className="mb-8">
              <Label className="text-brand-secondary font-semibold mb-4 block text-lg">Type de Formation *</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormationType('licence')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    formationType === 'licence' 
                      ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' 
                      : 'border-gray-200 hover:border-brand-primary/50'
                  }`}
                >
                  <div className="text-3xl mb-2">🎓</div>
                  <p className="font-bold text-lg">LICENCE</p>
                  <p className="text-sm text-gray-500">L1, L2, L3</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormationType('master')}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    formationType === 'master' 
                      ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' 
                      : 'border-gray-200 hover:border-brand-primary/50'
                  }`}
                >
                  <div className="text-3xl mb-2">🏆</div>
                  <p className="font-bold text-lg">MASTER</p>
                  <p className="text-sm text-gray-500">M1, M2</p>
                </button>
              </div>
            </div>

            {formationType && (
              <>
                {/* Nom et Prénom */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="nom" className="text-brand-secondary font-semibold mb-2 block">Nom *</Label>
                    <Input
                      id="nom"
                      name="nom"
                      data-testid="input-nom"
                      value={formData.nom}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="prenom" className="text-brand-secondary font-semibold mb-2 block">Prénom *</Label>
                    <Input
                      id="prenom"
                      name="prenom"
                      data-testid="input-prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      placeholder="Votre prénom"
                      className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                      required
                    />
                  </div>
                </div>

                {/* Email et WhatsApp */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-brand-secondary font-semibold mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Votre adresse email"
                      className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="whatsapp" className="text-brand-secondary font-semibold mb-2 block">WhatsApp *</Label>
                    <Input
                      id="whatsapp"
                      name="whatsapp"
                      data-testid="input-whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      placeholder="Numéro WhatsApp"
                      className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary"
                      required
                    />
                  </div>
                </div>

                {/* Campus et Dernier Diplôme */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="campus" className="text-brand-secondary font-semibold mb-2 block">Campus *</Label>
                    <Select 
                      value={formData.campus} 
                      onValueChange={(value) => handleSelectChange('campus', value)}
                    >
                      <SelectTrigger data-testid="select-campus" className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary">
                        <SelectValue placeholder="Choisissez un campus" />
                      </SelectTrigger>
                      <SelectContent>
                        {campus.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="dernier_diplome" className="text-brand-secondary font-semibold mb-2 block">Dernier Diplôme Obtenu *</Label>
                    <Select 
                      value={formData.dernier_diplome} 
                      onValueChange={(value) => handleSelectChange('dernier_diplome', value)}
                    >
                      <SelectTrigger data-testid="select-dernier-diplome" className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary">
                        <SelectValue placeholder="Votre dernier diplôme" />
                      </SelectTrigger>
                      <SelectContent>
                        {derniersDiplomes.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Filière et Diplôme Sollicité */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="filiere" className="text-brand-secondary font-semibold mb-2 block">
                      Filière {formationType === 'licence' ? 'Licence' : 'Master'} *
                    </Label>
                    <Select 
                      value={formData.filiere} 
                      onValueChange={(value) => handleSelectChange('filiere', value)}
                    >
                      <SelectTrigger data-testid="select-filiere" className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary">
                        <SelectValue placeholder="Choisissez une filière" />
                      </SelectTrigger>
                      <SelectContent>
                        {getCurrentFilieres().map((filiere) => (
                          <SelectItem key={filiere} value={filiere}>{filiere}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="diplome_sollicite" className="text-brand-secondary font-semibold mb-2 block">Niveau Sollicité *</Label>
                    <Select 
                      value={formData.diplome_sollicite} 
                      onValueChange={(value) => handleSelectChange('diplome_sollicite', value)}
                    >
                      <SelectTrigger data-testid="select-diplome-sollicite" className="border-gray-300 focus:border-brand-primary focus:ring-brand-primary">
                        <SelectValue placeholder="Niveau souhaité" />
                      </SelectTrigger>
                      <SelectContent>
                        {getCurrentDiplomesSollicites().map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="pt-6">
                  <Button
                    type="submit"
                    data-testid="submit-preinscription-btn"
                    disabled={loading}
                    className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-4 sm:py-6 text-base sm:text-lg font-semibold rounded-md transition-all hover:shadow-lg hover:-translate-y-1"
                  >
                    {loading ? 'Envoi en cours...' : 'Envoyer ma préinscription'}
                  </Button>
                </div>
              </>
            )}
          </form>
        </Card>

        <div className="mt-6 sm:mt-8 text-center text-sm sm:text-base text-gray-600">
          <p>* Champs obligatoires</p>
          <p className="mt-2">Vous serez contacté par votre conseiller d'orientation dans les plus brefs délais</p>
        </div>
      </div>
    </div>
  );
}
